import {
  Activity, CheckSquare, Clock, LucideIcon, Package, Shield, Thermometer, Wrench,
} from 'lucide-react';
import config from '../../configLoader';
import { PayrollEditEntryKind } from '../../interfaces/TimesheetEditInterface';
import { resolveHourlyModeConfig } from './hourlyModes';

/**
 * Payroll Timesheet Edit (M2-B) — the kind registry. One descriptor per entry
 * kind (the design bundle's KINDS shape, aligned to the implemented API §8):
 * a new tenant kind is a new entry here plus its modal — nothing else in the
 * editor changes (D9). Colors/backgrounds match PayrollSubmitWizard.KIND_META
 * (v2 tokens with tints precomputed to hex per the house convention).
 */

/** The editor's kinds: the seven API route segments plus 'pieceHours' — the 2020
 *  piece-hours TimeEntryHourly punch, a UI kind that saves through the `hourly` route. */
export type EditorKind = PayrollEditEntryKind | 'pieceHours';

export interface EntryKindDescriptor {
  /** Editor kind (the registry key). */
  kind: EditorKind;
  /** API route segment — POST/PUT/DELETE `/admin/timesheetEdit/{apiKind}[/:id]` (§8).
   *  Equals `kind` for every kind except 'pieceHours' (→ 'hourly'). */
  apiKind: PayrollEditEntryKind;
  /** Chip label. */
  label: string;
  /** Day-card section heading (design bundle vocabulary). */
  section: string;
  /** Lower-case noun for modal titles and the delete confirm ("hourly punch"). */
  noun: string;
  /** ChangeLogEntityType / entity table name. */
  entity: string;
  /** The ID key on `PayrollEntryResult` responses and entity rows. */
  idKey: string;
  /** punch = in/out clocks · amount = claimed $ · both = duration + $. */
  measure: 'punch' | 'amount' | 'both';
  color: string;
  bg: string;
  Icon: LucideIcon;
  order: number;
  /** `config.payrollTimesheetEditing.entryCapabilities` gate for this kind. 'pieceHours' is
   *  not a capability key — it is `hourlyEntries` AND clockHourlyV2Config's 'HourlyJob'. */
  capability:
    | 'hourlyEntries' | 'pieceHours' | 'pieceEntries' | 'taskClaims' | 'miscTasks'
    | 'cswTasks' | 'sickClaims' | 'operationalTasks';
}

export const ENTRY_KINDS: Record<EditorKind, EntryKindDescriptor> = {
  pieceHours: {
    kind: 'pieceHours', apiKind: 'hourly', label: 'Piece', section: 'Piece hours',
    noun: 'piece hours punch', entity: 'TimeEntryHourly', idKey: 'timeEntryHourlyID',
    measure: 'punch', color: '#2f6fed', bg: '#e8effd', Icon: Package, order: 0,
    capability: 'pieceHours',
  },
  hourly: {
    kind: 'hourly', apiKind: 'hourly', label: 'Hourly', section: 'Hourly punches', noun: 'hourly punch',
    entity: 'TimeEntryHourly', idKey: 'timeEntryHourlyID', measure: 'punch',
    color: '#1f9d6b', bg: '#e6f4ee', Icon: Clock, order: 1, capability: 'hourlyEntries',
  },
  piece: {
    kind: 'piece', apiKind: 'piece', label: 'Piece', section: 'Piece punches', noun: 'piece punch',
    entity: 'TimeEntry', idKey: 'timeEntryID', measure: 'punch',
    color: '#2f6fed', bg: '#e8effd', Icon: Package, order: 2, capability: 'pieceEntries',
  },
  task: {
    kind: 'task', apiKind: 'task', label: 'Task', section: 'Task claims', noun: 'task claim',
    entity: 'EmployeeTask', idKey: 'employeeTaskID', measure: 'amount',
    color: '#2f6fed', bg: '#e8effd', Icon: CheckSquare, order: 3, capability: 'taskClaims',
  },
  miscTask: {
    kind: 'miscTask', apiKind: 'miscTask', label: 'Misc', section: 'Misc tasks', noun: 'misc task',
    entity: 'MiscEmployeeTask', idKey: 'miscEmployeeTaskID', measure: 'amount',
    color: '#d8852f', bg: '#faf1e8', Icon: Wrench, order: 4, capability: 'miscTasks',
  },
  cswTask: {
    kind: 'cswTask', apiKind: 'cswTask', label: 'CSW', section: 'CSW tasks', noun: 'CSW claim',
    entity: 'EmployeeCustomerServiceWarranty', idKey: 'employeeCustomerServiceWarrantyID',
    measure: 'both', color: '#b1499c', bg: '#f6ebf4', Icon: Shield, order: 5,
    capability: 'cswTasks',
  },
  sickClaim: {
    kind: 'sickClaim', apiKind: 'sickClaim', label: 'Sick', section: 'Sick claims', noun: 'sick claim',
    entity: 'EmployeeSickClaim', idKey: 'employeeSickClaimID', measure: 'both',
    color: '#2fa3c2', bg: '#e8f4f8', Icon: Thermometer, order: 6, capability: 'sickClaims',
  },
  operationalTask: {
    kind: 'operationalTask', apiKind: 'operationalTask', label: 'Operational', section: 'Operational tasks',
    noun: 'operational task', entity: 'EmployeeOperationalTask',
    idKey: 'employeeOperationalTaskID', measure: 'both',
    color: '#6b7686', bg: '#eff1f3', Icon: Activity, order: 7,
    capability: 'operationalTasks',
  },
};

export const ENTRY_KIND_ORDER: EditorKind[] = (
  Object.keys(ENTRY_KINDS) as EditorKind[]
).sort((a, b) => ENTRY_KINDS[a].order - ENTRY_KINDS[b].order);

type EntryCapabilities = NonNullable<
  NonNullable<typeof config.payrollTimesheetEditing>['entryCapabilities']
>;

/**
 * The kinds this tenant's config enables, in render order. 'pieceHours' only
 * when hourly entries are on AND the tenant's clockHourlyV2Config enables
 * 'HourlyJob' — without that block the list is exactly the capability list.
 * Parameters exist for tests; the page calls it bare.
 */
export function enabledEntryKinds(
  caps: EntryCapabilities | null | undefined = config.payrollTimesheetEditing?.entryCapabilities,
  pieceHours: boolean = resolveHourlyModeConfig().pieceHours
): EditorKind[] {
  if (!caps) return [];
  return ENTRY_KIND_ORDER.filter((k) => {
    const cap = ENTRY_KINDS[k].capability;
    if (cap === 'pieceHours') return caps.hourlyEntries === true && pieceHours;
    return caps[cap] === true;
  });
}
